import { useEffect, useRef } from "react";
import { ApiError } from "./common/apiErrors";
import {
  DirectScriptGraphState,
  DirectScriptOutputResponse,
  RunStateSerializer,
  ScriptToSceneGraphState,
  Story2SceneGraphState
} from "./types/MeGenTypes";

/** คาบ polling เริ่มต้น (ms) — เปลี่ยนต่อการเรียกได้ด้วย option `interval` */
const DEFAULT_POLL_INTERVAL_MS = 30000;

/** หน่วงก่อนยิงนัดแรก (ms) เผื่อ isGenerating กระพริบใน render รอบเดียวกัน */
const INITIAL_POLL_DELAY_MS = 200;

type PollingEventMessage =
  | "HandleGetRunState"
  | "HandleGetStory2SceneState"
  | "HandleGetDirectScriptState"
  | "HandleGetScriptToSceneState";

type PollingSuccessMap = {
  HandleGetRunState: (state: RunStateSerializer, isGenerating: boolean) => void;
  HandleGetStory2SceneState: (state: Story2SceneGraphState, isGenerating: boolean) => void;
  HandleGetDirectScriptState: (state: DirectScriptGraphState, isGenerating: boolean, output?: DirectScriptOutputResponse) => void;
  HandleGetScriptToSceneState: (state: ScriptToSceneGraphState, isGenerating: boolean) => void;
};

export type PollingEvent = {
  [K in PollingEventMessage]: {
    message: K,
    params: {
      threadId: string;
      onSuccess?: PollingSuccessMap[K];
      onError?: (err: ApiError) => void;
    }
  }
}[PollingEventMessage];

type UseRunPollingOptions<T extends PollingEventMessage> = {
  threadId: string | null | undefined;
  isGenerating: boolean | undefined;
  eventMessage: T;
  onEvent: (e: PollingEvent) => void;
  onSuccess?: PollingSuccessMap[T];
  /** เรียกเมื่อ handler เจอ 4xx (run ถูกลบ / ไม่มีสิทธิ์) — ให้การ์ดปิด flag ฝั่งตัวเองที่ค้ำ polling ไว้
   *  ไม่งั้น flag ที่ clear เฉพาะใน onSuccess จะค้ำให้ยิงต่อทุก `interval` และ snack เด้งซ้ำทุกรอบ
   *  (network/5xx handler ไม่เรียกตัวนี้ — ปล่อยให้ลองรอบถัดไปเหมือนเดิม) */
  onError?: (err: ApiError) => void;
  interval?: number;
};

const useRunPolling = <T extends PollingEventMessage>({
  threadId,
  isGenerating,
  eventMessage,
  onEvent,
  onSuccess,
  onError,
  interval = DEFAULT_POLL_INTERVAL_MS,
}: UseRunPollingOptions<T>) => {
  const isPollingRef = useRef<boolean>(false);
  const onEventRef = useRef(onEvent);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  // Track the key for the current active polling session so a stale timeout
  // from a previous effect run cannot fire after the effect has been cleaned up.
  const sessionRef = useRef<number>(0);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!threadId || !isGenerating) {
      isPollingRef.current = false;
      return;
    }

    // Already polling for this session — don't restart.
    if (isPollingRef.current) return;

    isPollingRef.current = true;
    const session = ++sessionRef.current;

    let timeoutId: NodeJS.Timeout;

    const scheduleNext = () => {
      if (!isPollingRef.current || sessionRef.current !== session) return;
      timeoutId = setTimeout(fetchState, interval);
    };

    const fetchState = () => {
      if (!isPollingRef.current || sessionRef.current !== session) return;

      onEventRef.current?.({
        message: eventMessage,
        params: {
          threadId: threadId as string,
          onSuccess: onSuccessRef.current as any,
          onError: onErrorRef.current,
        },
      });

      // ตั้งรอบถัดไปทันทีหลัง dispatch (ไม่ได้รอ response) → คาบคงที่ทุก `interval` ms
      // ⚠ ไม่มีอะไรกัน request ซ้อนถ้า response ช้ากว่า interval; sessionRef กันแค่ timeout ค้างจาก session เก่า
      scheduleNext();
    };

    // หน่วงก่อนยิงนัดแรก เผื่อ isGenerating กระพริบ (เช่นใน render รอบเดียวกัน)
    // ถ้ากระพริบจริง cleanup จะ clearTimeout ตัวนี้ทิ้งก่อนถึงเวลา → ไม่มี request ซ้ำ
    timeoutId = setTimeout(fetchState, INITIAL_POLL_DELAY_MS);

    return () => {
      isPollingRef.current = false;
      clearTimeout(timeoutId);
    };
  }, [threadId, isGenerating, eventMessage, interval]);
};

export default useRunPolling;
